import { CONFIG } from "./config";
import { Auth } from "./auth";

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = await Auth.currentIdToken();
  const res = await fetch(`${CONFIG.apiBase}/${path}`, {
    ...init,
    headers: {
      ...(init.headers ?? {}),
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`API ${res.status}: ${body}`);
  }
  return res.json() as Promise<T>;
}

async function postJSON<T>(path: string, body: unknown, method = "POST"): Promise<T> {
  return request<T>(path, {
    method,
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export const api = {
  days: (from: string, to: string) =>
    request<{ days: any[]; from: string; to: string }>(`days?from=${from}&to=${to}`),
  patchMeal: (date: string, idx: number, body: any) =>
    postJSON(`days/${date}/meals/${idx}`, body, "PATCH"),
  postAdHocMeal: (date: string, body: any) =>
    postJSON(`days/${date}/meals`, body),
  patchWorkout: (date: string, body: any) =>
    postJSON(`days/${date}/workout`, body, "PATCH"),
  patchExercise: (date: string, idx: number, done: boolean) =>
    postJSON(`days/${date}/exercises/${idx}`, { done }, "PATCH"),
  patchPlan: (date: string, body: any) =>
    postJSON(`days/${date}/plan`, body, "PATCH"),
  postMetrics: (metrics: any[]) => postJSON(`metrics`, { metrics }),
  // Uploads: web app uses presigned PUTs the same way iOS does.
  createUpload: (body: any) => postJSON<{ id: string; uploadUrl: string; contentType: string }>(`uploads`, body),
  listUploads: (status: "pending" | "reviewed" | "all" = "pending") =>
    request<{ uploads: any[] }>(`uploads?status=${status}`),
  markReviewed: (id: string, note?: string) =>
    postJSON(`uploads/${id}/reviewed`, { reviewerNote: note ?? null }),
};

export async function uploadImage(file: File, payload: { dateISO: string; kind: string; mealIndex?: number; note?: string }) {
  const presign = await api.createUpload({
    ...payload,
    mealIndex: payload.mealIndex ?? null,
    contentType: file.type || "image/jpeg",
  });
  const put = await fetch(presign.uploadUrl, {
    method: "PUT",
    headers: { "Content-Type": presign.contentType },
    body: file,
  });
  if (!put.ok) throw new Error(`S3 PUT ${put.status}`);
  return presign.id;
}
